import React, { ChangeEvent, FC, useCallback, useState } from 'react';
import { Button, FormControl, Grid, IconButton, MenuItem, Switch } from '@material-ui/core';
import { DateRange } from '@material-ui/pickers';
import { CheckIcon, DateRangeSelect, FieldLabel, HamburgerIcon, InputControl, MultiSelectControl, SelectControl } from '../../../../../components';
import { useStyles } from './styles';
import { PartnerPartnership } from '../../../../../models';
import { useCommonStyles } from '../../../../../styles';
import { FiltersDialog } from './FiltersDialog';

interface Props {
  translation: any;
  partnerships: PartnerPartnership[];
  currencies: string[];
  selectedPartnerships: string[];
  selectedCurrencies: string[];
  showOnlyDepositsWithdrawals: boolean;
  showInactive: boolean;
  dateRange: DateRange<Date>;
  handleDateRangeChange: (date: DateRange<Date>) => void;
  onGroupByChange: (newOption: string) => void;
  onShowOnlyDepositsWithdrawalsChange: (show: boolean) => void;
  onShowInactiveChange: (showInactive: boolean) => void;
  onPartnershipCurrenciesChange: (newOptions: string[], type: string) => void;
  handleButtonClick: () => void;
  checkAllocation: (email: string | number) => void;
  handleFilterApplied: (currencies: string[], groupBy: string, showInactive: boolean, showOnlyDepositsWithdrawals: boolean, dateRange?: DateRange<Date>) => void;
}

export const TopSelectPanel: FC<Props> = ({
  translation,
  partnerships,
  selectedPartnerships,
  selectedCurrencies,
  currencies,
  dateRange,
  showInactive,
  showOnlyDepositsWithdrawals,
  onShowOnlyDepositsWithdrawalsChange,
  handleDateRangeChange,
  onGroupByChange,
  onPartnershipCurrenciesChange,
  onShowInactiveChange,
  handleButtonClick,
  handleFilterApplied,
  checkAllocation,
}) => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const [groupBy, setGroupBy] = useState<string>('noGrouping');
  const [selectedDateRange, setSelectedDateRange] = React.useState<DateRange<Date>>(dateRange);
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [email, setEmail] = useState<string | number>();

  const renderValueCustomCondition = useCallback(
    (value: string[]) => {
      if (value[0] === 'All') {
        return [translation.all];
      }

      return false;
    },
    [translation.all],
  );

  const handleSelectChange = useCallback(
    async (event, type) => {
      let options = event.target.value as string[];
      if (options.length === 0) {
        options = ['All'];
      }

      if (options.length > 1) {
        if (options.includes('All')) {
          if (options[options.length - 1] === 'All') {
            options = options.filter((option) => option === 'All');
          } else {
            options = options.filter((option) => option !== 'All');
          }
        }
      }

      onPartnershipCurrenciesChange(options, type);
    },
    [onPartnershipCurrenciesChange],
  );

  const selectGroupBy = useCallback(
    (event: ChangeEvent<{ value: any }>) => {
      const newValue = event.target.value;
      setGroupBy(newValue);
      onGroupByChange(newValue);
    },
    [onGroupByChange],
  );

  const handleInactiveSwitchChange = useCallback(
    async (event) => {
      event.stopPropagation();
      onShowInactiveChange(!showInactive);
    },
    [showInactive, onShowInactiveChange],
  );

  const handleOnlyDepositsWithdrawalsSwitchChange = useCallback(
    async (event) => {
      event.stopPropagation();
      onShowOnlyDepositsWithdrawalsChange(!showOnlyDepositsWithdrawals);
    },
    [showOnlyDepositsWithdrawals, onShowOnlyDepositsWithdrawalsChange],
  );

  const emailChange = (event: any) => {
    const { value } = event.target;
    setEmail(value);

    if (value === '') {
      checkAllocation(value);
    }
  };

  return (
    <>
      <Grid container direction="row" justify="space-between" alignContent="center" alignItems="center" spacing={1} className={classes.desktopContainer}>
        <Grid item xs={12}>
          <Grid container direction="column">
            <Grid container direction="row" justify="space-between" alignContent="center" alignItems="flex-end">
              <Grid item className={classes.filters}>
                {partnerships.length > 0 && (
                  <Grid container direction="column">
                    <FieldLabel label={translation.text_6432} />
                    <MultiSelectControl value={selectedPartnerships} onChange={(event: any) => handleSelectChange(event, 'Partnership')} renderValueCustomCondition={renderValueCustomCondition}>
                      <MenuItem className={selectedPartnerships.includes('All') ? classes.selectedOption : classes.option} value="All">
                        <CheckIcon />
                        <span>{translation.all}</span>
                      </MenuItem>
                      {partnerships.map((partnership) => (
                        <MenuItem
                          className={selectedPartnerships.includes(partnership.partnershipCode as string) ? classes.selectedOption : classes.option}
                          key={partnership.partnershipCode}
                          value={partnership.partnershipCode ? partnership.partnershipCode : ''}
                        >
                          <CheckIcon />
                          <span>{partnership.partnershipCode}</span>
                        </MenuItem>
                      ))}
                    </MultiSelectControl>
                  </Grid>
                )}
              </Grid>
              <Grid item className={classes.filters}>
                {currencies.length > 0 && (
                  <Grid container direction="column">
                    <FieldLabel label={translation.text_6433} />
                    <MultiSelectControl renderValueCustomCondition={renderValueCustomCondition} value={selectedCurrencies} onChange={(event: any) => handleSelectChange(event, 'Currency')}>
                      <MenuItem key="All" className={selectedCurrencies.includes('All') ? classes.selectedOption : classes.option} value="All">
                        <CheckIcon />
                        <span>{translation.all}</span>
                      </MenuItem>
                      {currencies.map((currency) => (
                        <MenuItem key={currency} className={selectedCurrencies.includes(currency) ? classes.selectedOption : classes.option} value={currency}>
                          <CheckIcon />
                          <span>{currency}</span>
                        </MenuItem>
                      ))}
                    </MultiSelectControl>
                  </Grid>
                )}
              </Grid>
              <Grid item className={classes.filters}>
                <FieldLabel label={translation.text_5718} />
                <FormControl variant="outlined" className={commonClasses.formControl}>
                  <SelectControl value={groupBy} onChange={selectGroupBy} minWidth={97}>
                    <MenuItem className={commonClasses.optionSelector} value="noGrouping">
                      {translation.no_grouping}
                    </MenuItem>
                    <MenuItem className={commonClasses.optionSelector} value="partnershipCode">
                      {translation.text_6432}
                    </MenuItem>
                    <MenuItem className={commonClasses.optionSelector} value="currency">
                      {translation.text_6433}
                    </MenuItem>
                    <MenuItem className={commonClasses.optionSelector} value="clientCountry">
                      {translation.text_6434}
                    </MenuItem>
                  </SelectControl>
                </FormControl>
              </Grid>
              <Grid item className={classes.emailField}>
                <FieldLabel label={translation.email_trading_account} />
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputControl value={email} placeholder={translation.specify} onChange={emailChange} />
                </FormControl>
              </Grid>
              <Grid item className={classes.btnContainer}>
                <Button
                  className={commonClasses.runReportBtn}
                  onClick={() => {
                    if (email) {
                      checkAllocation(email);
                    } else {
                      handleButtonClick();
                    }
                  }}
                >
                  {translation.run_report}
                </Button>
              </Grid>
            </Grid>
            <Grid container direction="row" alignContent="flex-start" alignItems="center" style={{ textAlign: 'left' }}>
              <Grid item xs={3}>
                <Switch checked={showInactive} classes={{ root: commonClasses.switchRoot }} onChange={handleInactiveSwitchChange} />
                <div className={classes.formControlLabel}>{translation.text_6431}</div>
              </Grid>
              <Grid item xs={5}>
                <Switch checked={showOnlyDepositsWithdrawals} classes={{ root: commonClasses.switchRoot }} onChange={handleOnlyDepositsWithdrawalsSwitchChange} />
                <div className={classes.formControlLabel}>{translation.show_only_clients_with_deposit_withdrawals}</div>
              </Grid>
              <Grid item xs={4}>
                {showOnlyDepositsWithdrawals && (
                  <>
                    <p className={commonClasses.formControlTitle}>{translation.text_6166}</p>
                    <DateRangeSelect
                      defaultValue={selectedDateRange}
                      onRangeChange={(value) => {
                        setSelectedDateRange(value);
                        handleDateRangeChange(value);
                      }}
                    />
                  </>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid container direction="row" justify="space-between" className={classes.mobileContainer} alignItems="center">
        <Grid item xs={11}>
          {partnerships.length > 0 && (
            <Grid container direction="column">
              <FieldLabel label={translation.text_6432} />
              <MultiSelectControl value={selectedPartnerships} onChange={(event: any) => handleSelectChange(event, 'Partnership')} renderValueCustomCondition={renderValueCustomCondition}>
                <MenuItem className={selectedPartnerships.includes('All') ? classes.selectedOption : classes.option} value="All">
                  <CheckIcon />
                  <span>{translation.all}</span>
                </MenuItem>
                {partnerships.map((partnership) => (
                  <MenuItem
                    className={selectedPartnerships.includes(partnership.partnershipCode as string) ? classes.selectedOption : classes.option}
                    key={partnership.partnershipCode}
                    value={partnership.partnershipCode ? partnership.partnershipCode : ''}
                  >
                    <CheckIcon />
                    <span>{partnership.partnershipCode}</span>
                  </MenuItem>
                ))}
              </MultiSelectControl>
            </Grid>
          )}
        </Grid>
        <Grid item xs={1}>
          <IconButton onClick={() => setOpenFilterDialog(true)}>
            <HamburgerIcon />
          </IconButton>
        </Grid>
      </Grid>

      <FiltersDialog
        open={openFilterDialog}
        translation={translation}
        dateRange={selectedDateRange}
        currencies={currencies}
        onClose={() => setOpenFilterDialog(false)}
        applyFilter={(selected, groupBy, inactive, onlyDepositsWithdrawals, dateRange) => {
          setOpenFilterDialog(false);
          if (dateRange) {
            setSelectedDateRange(dateRange);
          }
          handleFilterApplied(selected, groupBy, inactive, onlyDepositsWithdrawals, dateRange);
        }}
      />
    </>
  );
};
